import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '@mui/material/Tooltip';
import { Metric, Table, Expander, useToast, Layout } from './bpm';
import { useAuth } from '../contexts/AuthContext';
import { usePortfolio } from '../contexts/PortfolioContextIndex';
import { useTabRegistry } from '../contexts/TabRegistryContext';
import { useNotificationHistory } from '../contexts/NotificationHistoryContext';
import { useOliverName } from '../contexts/OliverContext';
import { registerDefaultTabs } from '../utils/registerDefaultTabs';
import { getPageIcon } from '../utils/pageIcons';
import appConfig from '../config/app.config';
import Sidebar from './Sidebar';
import Parametres from './Parametres';
import Synthese from './Synthese';
import Compte from './Compte';
import Portefeuille from './Portefeuille';
import StockScreener from './StockScreener';
import Analyse from './Analyse';
import Macro from './Macro';
import ActifDetail from './ActifDetail';
import AIChat from './AIChat/AIChat';
import NotificationBell from './NotificationBell';
import DashboardHeader from './DashboardHeader';
import ErrorBoundary from './ErrorBoundary';
import './Dashboard.css';
import './NotificationBell.css';

/** Met le texte en title case (première lettre de chaque mot en majuscule, reste en minuscule). */
function toTitleCase(str) {
  if (str == null || typeof str !== 'string' || !str.trim()) return str;
  return str.trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Dashboard() {
  // Charger la préférence de sidebar depuis localStorage, avec fallback sur la largeur d'écran
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarDefaultOpen');
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (e) {
      // Ignorer les erreurs de localStorage
    }
    // Fallback : ouvrir sur desktop, fermer sur mobile/tablette
    return window.innerWidth >= 640;
  });
  const [activePage, setActivePage] = useState('synthese');
  const [selectedActif, setSelectedActif] = useState(null);
  const [portfolioDefaultTab, setPortfolioDefaultTab] = useState(0);
  const [parametresDefaultTab, setParametresDefaultTab] = useState(0);
  const [logoUrl, setLogoUrl] = useState(() => {
    // Charger le logo depuis localStorage si disponible
    return localStorage.getItem('sidebarLogo') || null;
  });
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [isFooterAnimating, setIsFooterAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  const [oliverHistoryOpen, setOliverHistoryOpen] = useState(false);
  const [oliverNewDiscussionTrigger, setOliverNewDiscussionTrigger] = useState(0);
  const [oliverErrorKey, setOliverErrorKey] = useState(0);

  // Notifications mobile (même pattern d'animation que oliverHistoryOpen/historyClosing)
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifClosing, setNotifClosing] = useState(false);
  const [notifShowAll, setNotifShowAll] = useState(false);
  const { notifications: allNotifs, clearHistory: clearNotifHistory } = useNotificationHistory();
  const { oliverName } = useOliverName();
  const [notifMinLevel, setNotifMinLevel] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('notification_min_level') || '3', 10);
      return [1, 2, 3].includes(v) ? v : 3;
    } catch { return 3; }
  });
  /** Ids des icônes à afficher en barre mobile (null = toutes). Synchronisé avec Parametres > Apparence. */
  const [sidebarMobileItemIds, setSidebarMobileItemIds] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_mobile_items');
      if (!saved) return null;
      const arr = JSON.parse(saved);
      return Array.isArray(arr) ? arr : null;
    } catch { return null; }
  });
  /** Ids des icônes à afficher en sidebar desktop ouverte/fermée (null = toutes). Synchronisé avec Parametres > Apparence. */
  const [sidebarDesktopItemIds, setSidebarDesktopItemIds] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_desktop_items');
      if (!saved) return null;
      const arr = JSON.parse(saved);
      return Array.isArray(arr) ? arr : null;
    } catch { return null; }
  });
  useEffect(() => {
    const h = () => {
      try {
        const saved = localStorage.getItem('sidebar_mobile_items');
        setSidebarMobileItemIds(!saved ? null : (() => {
          try { const a = JSON.parse(saved); return Array.isArray(a) ? a : null; } catch { return null; }
        })());
      } catch { setSidebarMobileItemIds(null); }
    };
    window.addEventListener('sidebarMobileItemsUpdated', h);
    return () => window.removeEventListener('sidebarMobileItemsUpdated', h);
  }, []);
  useEffect(() => {
    const h = () => {
      try {
        const saved = localStorage.getItem('sidebar_desktop_items');
        setSidebarDesktopItemIds(!saved ? null : (() => {
          try { const a = JSON.parse(saved); return Array.isArray(a) ? a : null; } catch { return null; }
        })());
      } catch { setSidebarDesktopItemIds(null); }
    };
    window.addEventListener('sidebarDesktopItemsUpdated', h);
    return () => window.removeEventListener('sidebarDesktopItemsUpdated', h);
  }, []);
  useEffect(() => {
    const h = () => {
      try {
        const v = parseInt(localStorage.getItem('notification_min_level') || '3', 10);
        setNotifMinLevel([1, 2, 3].includes(v) ? v : 3);
      } catch { setNotifMinLevel(3); }
    };
    window.addEventListener('notificationLevelUpdated', h);
    return () => window.removeEventListener('notificationLevelUpdated', h);
  }, []);
  const filteredNotifs = useMemo(() =>
    allNotifs.filter(n => (n.level ?? 3) <= notifMinLevel), [allNotifs, notifMinLevel]
  );
  const notifCount = filteredNotifs.length;

  const handleNotifAnimEnd = useCallback((e) => {
    if (e.animationName === 'notif-mobile-close') {
      setNotifOpen(false);
      setNotifClosing(false);
      setNotifShowAll(false);
    }
  }, []);

  const requestCloseNotif = useCallback(() => {
    if (notifOpen && !notifClosing) setNotifClosing(true);
  }, [notifOpen, notifClosing]);

  const prevSidebarOpenRef = useRef(sidebarOpen);
  const { profile, logout, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const tabRegistry = useTabRegistry();
  const { 
    isConnected, 
    account, 
    positions, 
    loading, 
    connect, 
    disconnect, 
    refreshData 
  } = usePortfolio();

  // Appliquer le thème au chargement (manuel ou automatique selon préférence système)
  useEffect(() => {
    const applyTheme = () => {
      try {
        const auto = localStorage.getItem('darkThemeAuto') === 'true';
        const dark = auto
          ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches)
          : (localStorage.getItem('darkTheme') === 'true');
        if (dark) {
          document.documentElement.classList.add('theme-dark');
        } else {
          document.documentElement.classList.remove('theme-dark');
        }
        const metaTheme = document.querySelector('meta[name="theme-color"]');
        if (metaTheme) metaTheme.setAttribute('content', dark ? '#1a1a1a' : '#ffffff');
      } catch (e) {
        // Ignorer
      }
    };
    applyTheme();
    const mq = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const onSchemeChange = () => {
      if (localStorage.getItem('darkThemeAuto') === 'true') applyTheme();
    };
    if (mq) mq.addEventListener('change', onSchemeChange);
    window.addEventListener('themeUpdated', applyTheme);
    return () => {
      if (mq) mq.removeEventListener('change', onSchemeChange);
      window.removeEventListener('themeUpdated', applyTheme);
    };
  }, []);

  // Enregistrer les onglets par défaut au montage (une seule fois)
  const tabsRegisteredRef = useRef(false);
  useEffect(() => {
    if (!tabsRegisteredRef.current) {
      const { registerTabs } = tabRegistry;
      registerDefaultTabs({ registerTabs });
      tabsRegisteredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Ne s'exécute qu'une seule fois au montage

  // Mettre à jour les libellés des onglets AI quand le nom d'Oliver change
  const { registerTab } = tabRegistry;
  useEffect(() => {
    registerTab({ id: 'ai', label: oliverName, parent: null, level: 1, children: ['ai-chat'] });
    registerTab({ id: 'ai-chat', label: `Chat ${oliverName}`, parent: 'ai', level: 2 });
  }, [oliverName, registerTab]);

  // Rediriger vers la page de connexion si non authentifié
  useEffect(() => {
    if (!isAuthenticated) {
      // L'App.js gère déjà l'affichage de LoginPage
      // On peut juste s'assurer que le Dashboard ne s'affiche pas
    }
  }, [isAuthenticated]);

  // Détecter les changements d'état de la sidebar pour déclencher l'animation uniquement lors de la transition
  useEffect(() => {
    // Au premier montage, initialiser la référence et ne pas déclencher l'animation
    if (isInitialMount) {
      prevSidebarOpenRef.current = sidebarOpen;
      setIsInitialMount(false);
      return;
    }

    // Détecter un changement d'état (ouverture de la sidebar)
    if (sidebarOpen && !prevSidebarOpenRef.current) {
      // La sidebar vient de s'ouvrir (elle était fermée avant)
      setIsFooterAnimating(true);
      
      // Réinitialiser l'état de transition après l'animation
      const timer = setTimeout(() => {
        setIsFooterAnimating(false);
      }, 500); // Durée de l'animation + un peu de marge

      prevSidebarOpenRef.current = sidebarOpen;
      return () => clearTimeout(timer);
    } else {
      // Mettre à jour la référence même si pas d'animation
      prevSidebarOpenRef.current = sidebarOpen;
      // Si la sidebar se ferme, réinitialiser immédiatement
      setIsFooterAnimating(false);
    }
  }, [sidebarOpen, isInitialMount]);

  // Fermer automatiquement la sidebar et détecter le mode mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // IDs des sous-onglets Paramètres (ils n'utilisent pas le préfixe "parametres-")
  const PARAMETRES_CHILD_IDS = ['compte-settings', 'configuration', 'apparence', 'valeurs-par-defaut', 'api', 'import-export', 'utilisateurs', 'administration', 'configuration-admin', 'statuts'];
  
  const hasAccessToTab = useCallback((tabId, allowed) => {
    if (tabId === 'parametres') {
      return allowed.includes(tabId) || allowed.some(id => PARAMETRES_CHILD_IDS.includes(id));
    }
    if (['portefeuille', 'compte', 'analyse', 'macro'].includes(tabId)) {
      return allowed.some(id => id === tabId || id.startsWith(tabId + '-'));
    }
    return allowed.includes(tabId);
  }, []);

  // Rediriger si l'utilisateur accède à une page non autorisée (accès restreints)
  const activePageToTabId = { synthese: 'accueil', portfolio: 'portefeuille', 'actif-detail': 'portefeuille', compte: 'compte', 'stock-screener': 'stock-screener', analyse: 'analyse', macro: 'macro', ai: 'ai', parametres: 'parametres' };
  useEffect(() => {
    if (!profile || !isAuthenticated) return;
    if (profile.is_superadmin || profile.is_owner) return; // Accès complet
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return; // Tout autorisé
    const tabId = activePageToTabId[activePage];
    if (!tabId) return;
    const hasAccess = hasAccessToTab(tabId, allowed);
    if (!hasAccess) {
      const tabToPage = { accueil: 'synthese', portefeuille: 'portfolio', compte: 'compte', 'stock-screener': 'stock-screener', analyse: 'analyse', macro: 'macro', ai: 'ai', parametres: 'parametres' };
      const firstAllowed = ['accueil', 'portefeuille', 'compte', 'stock-screener', 'analyse', 'macro', 'ai', 'parametres']
        .find(id => hasAccessToTab(id, allowed));
      setActivePage(tabToPage[firstAllowed] || 'synthese');
    }
  }, [profile, isAuthenticated, activePage, hasAccessToTab]);

  // Fonction pour mettre à jour le logo (sera utilisée depuis les paramètres)
  useEffect(() => {
    const handleLogoUpdate = (event) => {
      const newLogoUrl = event.detail;
      setLogoUrl(newLogoUrl);
      if (newLogoUrl) {
        localStorage.setItem('sidebarLogo', newLogoUrl);
      } else {
        localStorage.removeItem('sidebarLogo');
      }
    };

    window.addEventListener('logoUpdated', handleLogoUpdate);
    return () => window.removeEventListener('logoUpdated', handleLogoUpdate);
  }, []);

  const handleConnect = async () => {
    const success = await connect();
    if (success) {
      showToast('La connexion à IB Gateway a été établie avec succès.', 'success', 3000, 'Connexion IB Gateway', 'Synthese', getPageIcon('Synthese'));
    } else {
      showToast('Vérifiez que IB Gateway est lancé et que les connexions API sont activées.', 'error', 3000, 'Échec de connexion IB Gateway', 'Synthese', getPageIcon('Synthese'));
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    showToast('Vous avez été déconnecté d\'IB Gateway.', 'info', 3000, 'Déconnexion', 'Synthese', getPageIcon('Synthese'));
  };

  // Filtrer les onglets selon allowed_tabs de l'utilisateur (null/vide = accès à tout)
  const canAccessTab = useCallback((tabIdOrIds) => {
    if (!profile) return false;
    if (profile.is_superadmin || profile.is_owner) return true;
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return true;
    const ids = Array.isArray(tabIdOrIds) ? tabIdOrIds : [tabIdOrIds];
    return ids.some(id => {
      if (id === 'parametres') return hasAccessToTab(id, allowed);
      if (['portefeuille', 'compte', 'analyse', 'macro', 'ai'].includes(id)) return allowed.some(a => a === id || a.startsWith(id + '-'));
      return allowed.includes(id);
    });
  }, [profile?.allowed_tabs, profile?.is_superadmin, profile?.is_owner, hasAccessToTab]);

  // BPM Phase 5 : filtre sidebar/tabs par requiredPermission (permissions dérivées du rôle côté backend)
  const hasPermission = useCallback((perm) => {
    if (!profile?.permissions) return true; // rétrocompat : pas de permissions = tout autoriser
    return profile.permissions.includes('*') || profile.permissions.includes(perm);
  }, [profile?.permissions]);

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'JPY': '¥',
      'CHF': 'CHF',
      'CAD': 'C$',
      'AUD': 'A$',
    };
    return symbols[currency] || currency;
  };

  const formatCurrency = (value, currency = 'EUR', decimals = 2) => {
    // Format avec virgule pour décimales et espaces pour milliers
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
    const symbol = getCurrencySymbol(currency);
    return `${formatted} ${symbol}`;
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  };

  // Colonnes du tableau positions
  const positionsColumns = [
    { key: 'symbol', label: 'Symbole', align: 'left' },
    { key: 'secType', label: 'Type', align: 'left' },
    { 
      key: 'position', 
      label: 'Quantité', 
      align: 'right',
      render: (val) => val.toFixed(0)
    },
    { 
      key: 'avgCost', 
      label: 'Prix Moyen', 
      align: 'right',
      render: (val, row) => formatCurrency(val, row.currency)
    },
    { 
      key: 'marketPrice', 
      label: 'Prix Actuel', 
      align: 'right',
      render: (val, row) => formatCurrency(val, row.currency)
    },
    { 
      key: 'marketValue', 
      label: 'Valeur', 
      align: 'right',
      render: (val, row) => formatCurrency(val, row.currency)
    },
    { 
      key: 'unrealizedPNL', 
      label: 'P&L', 
      align: 'right',
      render: (val, row) => (
        <span style={{ color: val >= 0 ? '#28a745' : '#dc3545', fontWeight: 600 }}>
          {formatCurrency(val, row.currency)}
        </span>
      )
    },
  ];

  // Menu compact (sidebar fermée desktop) / barre mobile : même ordre et visibilité que la nav, avec icônes en HTML pour affichage
  // En mobile : filtre optionnel par sidebarMobileItemIds (Parametres > Apparence > Barre mobile)
  // En desktop (ouverte/fermée) : filtre optionnel par sidebarDesktopItemIds (Parametres > Apparence > Barre desktop)
  const compactMenuItems = useMemo(() => {
    const SIDEBAR_ID_TO_PAGE = { accueil: 'synthese', portefeuille: 'portfolio', compte: 'compte', 'stock-screener': 'stock-screener', analyse: 'analyse', macro: 'macro', ai: 'ai', parametres: 'parametres' };
    let items = (appConfig.sidebar?.items || [])
      .map((item) => {
        const pageKey = SIDEBAR_ID_TO_PAGE[item.id];
        if (!pageKey) return null;
        if (item.requiredPermission && !hasPermission(item.requiredPermission)) return null;
        const tabsToCheck = [item.id, ...(item.children || [])];
        const show = item.id === 'compte' ? (canAccessTab(tabsToCheck) || isMobile) : item.id === 'stock-screener' ? (!isMobile && canAccessTab(tabsToCheck)) : canAccessTab(tabsToCheck);
        if (!show) return null;
        if (isMobile && Array.isArray(sidebarMobileItemIds) && sidebarMobileItemIds.length > 0 && !sidebarMobileItemIds.includes(item.id)) return null;
        if (!isMobile && Array.isArray(sidebarDesktopItemIds) && sidebarDesktopItemIds.length > 0 && !sidebarDesktopItemIds.includes(item.id)) return null;
        const label = item.id === 'ai' ? oliverName : item.label;
        const iconSvg = getPageIcon(item.label);
        const handleClick = (e) => {
          e.preventDefault();
          if (item.id === 'portefeuille') {
            if (activePage === 'actif-detail') setPortfolioDefaultTab(1);
            else setPortfolioDefaultTab(0);
          } else if (item.id === 'parametres') setParametresDefaultTab(1);
          setActivePage(pageKey);
        };
        const isActive = activePage === pageKey || (pageKey === 'portfolio' && activePage === 'actif-detail');
        return { id: item.id, label, iconSvg, onClick: handleClick, active: isActive, isParametres: item.id === 'parametres' };
      })
      .filter(Boolean);
    return items;
  }, [appConfig.sidebar?.items, activePage, oliverName, isMobile, canAccessTab, hasPermission, sidebarMobileItemIds, sidebarDesktopItemIds]);

  const sidebarProps = {
    logoUrl,
    profile: isAuthenticated ? profile : null,
    onAvatarClick: () => { setParametresDefaultTab(0); setActivePage('parametres'); },
    isConnected,
    onLogoClick: () => setActivePage('synthese'),
    activePage,
    compactMenuItems,
  };

  const desktopHeader = !isMobile && (
    <DashboardHeader
      title={
        activePage === 'synthese' ? 'Accueil' :
        activePage === 'portfolio' ? 'Portefeuille' :
        activePage === 'actif-detail' ? toTitleCase(selectedActif?.name || selectedActif?.symbol || 'Détail actif') :
        activePage === 'compte' ? 'Compte' :
        activePage === 'stock-screener' ? 'Stock screener' :
        activePage === 'analyse' ? 'Analyse' :
        activePage === 'macro' ? 'Macro' :
        activePage === 'ai' ? oliverName :
        activePage === 'parametres' ? 'Paramètres' :
        'Résumé du Compte'
      }
      logoUrl={logoUrl}
      onLogoClick={() => setActivePage('synthese')}
    >
      {activePage === 'ai' && canAccessTab(['ai', 'ai-chat']) && (
        <>
          <button type="button" className="dashboard-header-icon-btn" onClick={() => setOliverNewDiscussionTrigger((t) => t + 1)} aria-label="Nouvelle discussion">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f" aria-hidden><path d="m98.46-98.46 62.62-212.62q-19-40.23-30.04-82.47T120-480q0-74.7 28.34-140.4t76.92-114.3q48.58-48.6 114.26-76.95Q405.19-840 479.87-840q74.67 0 140.41 28.34 65.73 28.34 114.36 76.92 48.63 48.58 76.99 114.26Q840-554.81 840-480.13q0 74.67-28.35 140.41-28.35 65.73-76.95 114.36-48.6 48.63-114.3 76.99Q554.7-120 480-120q-44.21 0-86.45-11.04t-82.47-30.04L98.46-98.46ZM158-158l128-38q15.54-4 28.5-3.77 12.96.23 27.5 7.77 32 16 67 24t71 8q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 36 8 71t24 67q7 13 7.88 27.5.89 14.5-3.88 28.5l-38 128Zm302-182h40v-120h120v-40H500v-120h-40v120H340v40h120v120Zm19-139Z"/></svg>
          </button>
          <button type="button" className="dashboard-header-icon-btn" onClick={() => setOliverHistoryOpen((prev) => !prev)} aria-label={`Historique des échanges avec ${oliverName}`}>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" aria-hidden><path d="M208.73-548.69q-8.73-8.68-8.73-21.27 0-12.58 8.69-21.31 8.68-8.73 21.27-8.73 12.58 0 21.31 8.69 8.73 8.68 8.73 21.27 0 12.58-8.69 21.31-8.69 8.73-21.27 8.73t-21.31-8.69Zm500 0q-8.73-8.68-8.73-21.27 0-12.58 8.69-21.31 8.69-8.73 21.27-8.73t21.31 8.69q8.73 8.68 8.73 21.27 0 12.58-8.69 21.31-8.68 8.73-21.27 8.73-12.58 0-21.31-8.69ZM80-460Zm800 0ZM160-120h-40v-120h160q-58.31 0-99.15-40.85Q140-321.69 140-380h40q0 41.25 29.37 70.62Q238.75-280 280-280v-160h108l-38-155q-23-92-98.5-148.5T80-800v-40q109.92 0 195.88 64.92 85.97 64.93 112.66 170.85l41.47 164.17q3.99 15.21-6.09 27.64Q413.85-400 398.46-400H320v135.38q0 26.66-18.98 45.64T255.38-200H160v80Zm680 0h-40v-80h-95.38q-26.66 0-45.64-18.98T640-264.62V-400h-78.46q-15.39 0-25.46-12.54-10.08-12.54-5.62-27.92l41-163.77q27.39-105 112.77-170.39Q769.62-840 880-840v40q-95.77 0-171.27 56.62-75.5 56.61-98.5 148.38L572-440h108v160q41.46-.77 70.73-29.65Q780-338.54 780-380h40q0 58.31-40.85 98.77Q738.31-240.77 680-240h160v120ZM320-240v-40 40Zm320 0v-40 40Z"/></svg>
          </button>
        </>
      )}
      <NotificationBell onPanelOpen={() => setOliverHistoryOpen(false)} />
    </DashboardHeader>
  );

  const mobileHeaderPortal = isMobile ? (
    <ErrorBoundary fallback={<div className="dashboard-header-error-fallback" style={{ padding: '8px 12px', background: '#fff3e0', color: '#e65100', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}><span>Erreur d'affichage</span><button type="button" onClick={() => window.location.reload()} style={{ padding: '4px 12px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Recharger</button></div>}>
      <DashboardHeader
        title={activePage === 'synthese' ? 'Accueil' : activePage === 'portfolio' ? 'Portefeuille' : activePage === 'actif-detail' ? toTitleCase(selectedActif?.name || selectedActif?.symbol || 'Détail actif') : activePage === 'compte' ? 'Compte' : activePage === 'stock-screener' ? 'Stock screener' : activePage === 'analyse' ? 'Analyse' : activePage === 'macro' ? 'Macro' : activePage === 'ai' ? oliverName : activePage === 'parametres' ? 'Paramètres' : 'Résumé du Compte'}
        logoUrl={logoUrl}
        onLogoClick={() => setActivePage('synthese')}
      >
        {activePage === 'ai' && canAccessTab(['ai', 'ai-chat']) && (
          <>
            <button type="button" className="dashboard-header-icon-btn" onClick={() => setOliverNewDiscussionTrigger((t) => t + 1)} aria-label="Nouvelle discussion"><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f" aria-hidden><path d="m98.46-98.46 62.62-212.62q-19-40.23-30.04-82.47T120-480q0-74.7 28.34-140.4t76.92-114.3q48.58-48.6 114.26-76.95Q405.19-840 479.87-840q74.67 0 140.41 28.34 65.73 28.34 114.36 76.92 48.63 48.58 76.99 114.26Q840-554.81 840-480.13q0 74.67-28.35 140.41-28.35 65.73-76.95 114.36-48.6 48.63-114.3 76.99Q554.7-120 480-120q-44.21 0-86.45-11.04t-82.47-30.04L98.46-98.46ZM158-158l128-38q15.54-4 28.5-3.77 12.96.23 27.5 7.77 32 16 67 24t71 8q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 36 8 71t24 67q7 13 7.88 27.5.89 14.5-3.88 28.5l-38 128Zm302-182h40v-120h120v-40H500v-120h-40v120H340v40h120v120Zm19-139Z"/></svg></button>
            <button type="button" className="dashboard-header-icon-btn" onClick={() => setOliverHistoryOpen((prev) => !prev)} aria-label={`Historique des échanges avec ${oliverName}`}><svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" aria-hidden><path d="M208.73-548.69q-8.73-8.68-8.73-21.27 0-12.58 8.69-21.31 8.68-8.73 21.27-8.73 12.58 0 21.31 8.69 8.73 8.68 8.73 21.27 0 12.58-8.69 21.31-8.69 8.73-21.27 8.73t-21.31-8.69Zm500 0q-8.73-8.68-8.73-21.27 0-12.58 8.69-21.31 8.69-8.73 21.27-8.73t21.31 8.69q8.73 8.68 8.73 21.27 0 12.58-8.69 21.31-8.68 8.73-21.27 8.73-12.58 0-21.31-8.69ZM80-460Zm800 0ZM160-120h-40v-120h160q-58.31 0-99.15-40.85Q140-321.69 140-380h40q0 41.25 29.37 70.62Q238.75-280 280-280v-160h108l-38-155q-23-92-98.5-148.5T80-800v-40q109.92 0 195.88 64.92 85.97 64.93 112.66 170.85l41.47 164.17q3.99 15.21-6.09 27.64Q413.85-400 398.46-400H320v135.38q0 26.66-18.98 45.64T255.38-200H160v80Zm680 0h-40v-80h-95.38q-26.66 0-45.64-18.98T640-264.62V-400h-78.46q-15.39 0-25.46-12.54-10.08-12.54-5.62-27.92l41-163.77q27.39-105 112.77-170.39Q769.62-840 880-840v40q-95.77 0-171.27 56.62-75.5 56.61-98.5 148.38L572-440h108v160q41.46-.77 70.73-29.65Q780-338.54 780-380h40q0 58.31-40.85 98.77Q738.31-240.77 680-240h160v120ZM320-240v-40 40Zm320 0v-40 40Z"/></svg></button>
          </>
        )}
        <button type="button" className="dashboard-header-icon-btn notification-bell-mobile-btn" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { if (!notifOpen && !notifClosing) { setNotifOpen(true); setOliverHistoryOpen(false); } else if (notifOpen && !notifClosing) requestCloseNotif(); }} aria-label="Notifications">
          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor" aria-hidden><path d="M200-209.23v-40h64.62v-316.92q0-78.39 49.61-137.89 49.62-59.5 125.77-74.11V-800q0-16.67 11.64-28.33Q463.28-840 479.91-840t28.36 11.67Q520-816.67 520-800v21.85q76.15 14.61 125.77 74.11 49.61 59.5 49.61 137.89v316.92H760v40H200Zm280-286.15Zm-.14 390.76q-26.71 0-45.59-18.98-18.89-18.98-18.89-45.63h129.24q0 26.85-19.03 45.73-19.02 18.88-45.73 18.88ZM304.62-249.23h350.76v-316.92q0-72.93-51.23-124.16-51.23-51.23-124.15-51.23-72.92 0-124.15 51.23-51.23 51.23-51.23 124.16v316.92Z"/></svg>
          {notifCount > 0 && <span className="notification-bell-badge">{notifCount > 99 ? '99+' : notifCount}</span>}
        </button>
      </DashboardHeader>
    </ErrorBoundary>
  ) : null;

  return (
    <>
      <Layout
        config={appConfig}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        sidebarProps={sidebarProps}
        headerContent={desktopHeader}
        headerContentPortal={mobileHeaderPortal}
        isMobile={isMobile}
        contentClassName={activePage === 'ai' ? 'dashboard-page-content--ai' : ''}
        sidebarContent={(
        <>
        <div className="sidebar-main">
          <nav className="sidebar-nav">
            <ul>
              {(appConfig.sidebar?.items || []).map((item) => {
                const SIDEBAR_ID_TO_PAGE = { accueil: 'synthese', portefeuille: 'portfolio', compte: 'compte', 'stock-screener': 'stock-screener', analyse: 'analyse', macro: 'macro', ai: 'ai', parametres: 'parametres' };
                const pageKey = SIDEBAR_ID_TO_PAGE[item.id];
                if (!pageKey) return null;
                if (item.requiredPermission && !hasPermission(item.requiredPermission)) return null;
                if (!isMobile && Array.isArray(sidebarDesktopItemIds) && sidebarDesktopItemIds.length > 0 && !sidebarDesktopItemIds.includes(item.id)) return null;
                const tabsToCheck = [item.id, ...(item.children || [])];
                const show = item.id === 'compte' ? (canAccessTab(tabsToCheck) || isMobile) : item.id === 'stock-screener' ? (!isMobile && canAccessTab(tabsToCheck)) : canAccessTab(tabsToCheck);
                if (!show) return null;
                const label = item.id === 'ai' ? oliverName : item.label;
                const iconSvg = getPageIcon(item.label);
                const handleClick = (e) => {
                  e.preventDefault();
                  if (item.id === 'portefeuille') {
                    if (activePage === 'actif-detail') setPortfolioDefaultTab(1);
                    else setPortfolioDefaultTab(0);
                  } else if (item.id === 'parametres') setParametresDefaultTab(1);
                  setActivePage(pageKey);
                };
                const isActive = activePage === pageKey || (pageKey === 'portfolio' && activePage === 'actif-detail');
                return (
                  <li key={item.id}>
                    <a href={`#${pageKey}`} className={isActive ? 'active' : ''} onClick={handleClick} {...(item.id === 'parametres' ? { style: { position: 'relative', alignItems: 'center' } } : {})}>
                      {item.id === 'parametres' ? (
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          {iconSvg ? <span className="sidebar-nav-icon" dangerouslySetInnerHTML={{ __html: iconSvg }} /> : null}
                          <span style={{ position: 'absolute', bottom: '2px', right: 0, width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isConnected ? '#34C759' : '#FF3B30', border: '1px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} title={isConnected ? 'Connecté à IB Gateway' : 'Déconnecté de IB Gateway'} />
                        </span>
                      ) : (iconSvg ? <span className="sidebar-nav-icon" dangerouslySetInnerHTML={{ __html: iconSvg }} /> : null)}
                      {label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
        {isAuthenticated && profile && (
          <div className={`sidebar-footer ${isFooterAnimating && sidebarOpen ? 'sidebar-footer-animate' : ''}`}>
            <div className="sidebar-user">
              <div 
                key={`avatar-${profile.user_id || profile.id || profile.email}`}
                className="sidebar-user-avatar"
                onClick={(e) => {
                  e.preventDefault();
                  setParametresDefaultTab(0); // Index de l'onglet "Compte" dans Parametres
                  setActivePage('parametres');
                }}
                style={{ cursor: 'pointer' }}
              >
                {profile.picture ? (
                  <img 
                    src={profile.picture.startsWith('data:') ? profile.picture : `${profile.picture}?t=${Date.now()}`}
                    alt={profile.name || 'User'}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const initialsDiv = e.target.nextSibling;
                      if (initialsDiv) {
                        initialsDiv.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="sidebar-user-initials"
                  style={{ display: profile.picture ? 'none' : 'flex' }}
                >
                  {(() => {
                    const name = profile.name || 'User';
                    return name.split(/\s+/).map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                  })()}
                </div>
              </div>
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{profile.name || 'Utilisateur'}</div>
                <div className="sidebar-user-email">{profile.email || ''}</div>
              </div>
            </div>
            <button className="sidebar-logout-btn" onClick={logout}>
              Se déconnecter
            </button>
          </div>
        )}
        </>
        )}
      >
        {isMobile && (notifOpen || notifClosing) && createPortal(
          <div
            onClick={(e) => { if (e.target === e.currentTarget) requestCloseNotif(); }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              width: '100vw', height: '100vh',
              zIndex: 99999,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
              paddingTop: 'calc(72px + env(safe-area-inset-top, 0px))',
              paddingRight: '16px', paddingLeft: '16px',
              boxSizing: 'border-box', background: 'transparent',
            }}
          >
            <div
              className={notifClosing ? 'notif-mobile-closing' : 'notif-mobile-opening'}
              onClick={(e) => e.stopPropagation()}
              onAnimationEnd={handleNotifAnimEnd}
              style={{
                width: '100%', maxWidth: 'calc(100vw - 32px)',
                maxHeight: '600px',
                background: 'var(--bg-primary, #fff)',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border-color, #ddd)', background: 'var(--header-bg, rgb(246,246,246))', flexShrink: 0, minHeight: '48px', boxSizing: 'border-box' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--text-color, #333)' }}>Notifications</h3>
                {notifCount > 0 && (
                  <button
                    type="button"
                    onClick={() => { if (window.confirm('Voulez-vous effacer tout l\'historique des notifications ?')) clearNotifHistory(); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #666)', cursor: 'pointer', fontSize: '14px', padding: '4px 8px', borderRadius: '4px' }}
                  >
                    Effacer
                  </button>
                )}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {filteredNotifs.length === 0 ? (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-secondary, #666)' }}>Aucune notification</div>
                ) : (
                  <>
                    {(notifShowAll ? filteredNotifs : filteredNotifs.slice(0, 10)).map((n) => {
                      const display = (n.pageName || !(/token\s*(invalide|expiré|invalid|expired)|invalide\s*ou\s*expiré|401|unauthorized/i.test(n.message || '')))
                        ? n
                        : { ...n, pageName: oliverName, title: 'Problème de token' };
                      const typeClass = (n.type && ['success', 'error', 'warning', 'info'].includes(n.type)) ? `notification-item--${n.type}` : 'notification-item--info';
                      return (
                        <div key={n.id} className={`notification-item ${typeClass}`}>
                          <div className="notification-item-content">
                            {display.pageName && (
                              <div className="notification-item-page">
                                {display.pageIcon && typeof display.pageIcon === 'string' && display.pageIcon.startsWith('<svg') && (
                                  <span className="notification-item-page-icon" dangerouslySetInnerHTML={{ __html: display.pageIcon }} />
                                )}
                                <span className="notification-item-page-name">{display.pageName}</span>
                              </div>
                            )}
                            {display.title && <div className="notification-item-title">{display.title}</div>}
                            <div className="notification-item-message">{display.message}</div>
                            <div className="notification-item-time">
                              {(() => {
                                const d = new Date(n.timestamp), now = new Date();
                                const mins = Math.floor((now - d) / 60000);
                                if (mins < 1) return 'À l\'instant';
                                if (mins < 60) return `Il y a ${mins} min`;
                                const hrs = Math.floor(mins / 60);
                                if (hrs < 24) return `Il y a ${hrs}h`;
                                const days = Math.floor(hrs / 24);
                                if (days < 7) return `Il y a ${days}j`;
                                return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {filteredNotifs.length > 10 && !notifShowAll && (
                      <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid var(--border-color, #eee)', background: 'var(--header-bg, rgb(246,246,246))', flexShrink: 0 }}>
                        <button type="button" onClick={() => setNotifShowAll(true)} style={{ background: 'none', border: 'none', color: 'var(--accent-color, #A49B6D)', cursor: 'pointer', fontSize: '14px', fontWeight: 500, padding: '4px 8px' }}>
                          Afficher toutes les notifications ({filteredNotifs.length})
                        </button>
                      </div>
                    )}
                    {notifShowAll && filteredNotifs.length > 10 && (
                      <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid var(--border-color, #eee)', background: 'var(--header-bg, rgb(246,246,246))', flexShrink: 0 }}>
                        <button type="button" onClick={() => setNotifShowAll(false)} style={{ background: 'none', border: 'none', color: 'var(--accent-color, #A49B6D)', cursor: 'pointer', fontSize: '14px', fontWeight: 500, padding: '4px 8px' }}>
                          Afficher moins
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
        {activePage === 'synthese' ? (
          <Synthese onLogoClick={() => setActivePage('synthese')} />
        ) : activePage === 'portfolio' ? (
          <Portefeuille 
            onActifClick={(actif) => {
              setSelectedActif(actif);
              setActivePage('actif-detail');
            }}
            defaultTab={portfolioDefaultTab}
            onLogoClick={() => setActivePage('synthese')}
          />
        ) : activePage === 'actif-detail' ? (
          <ActifDetail 
            actif={selectedActif} 
            onBack={() => setActivePage('portfolio')} 
            onLogoClick={() => setActivePage('synthese')}
          />
        ) : activePage === 'compte' ? (
          <Compte onLogoClick={() => setActivePage('synthese')} />
        ) : activePage === 'stock-screener' ? (
          <StockScreener onLogoClick={() => setActivePage('synthese')} />
        ) : activePage === 'analyse' ? (
          <Analyse onLogoClick={() => setActivePage('synthese')} />
        ) : activePage === 'macro' ? (
          <Macro onLogoClick={() => setActivePage('synthese')} />
        ) : activePage === 'ai' ? (
          <div className="dashboard-ai-chat-wrapper">
            <ErrorBoundary
              key={oliverErrorKey}
              fallback={
                <div className="dashboard-oliver-error-fallback" style={{ padding: '24px', textAlign: 'center', background: 'var(--surface)', borderRadius: '8px', margin: '16px' }}>
                  <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)' }}>{oliverName} a rencontré une erreur (par ex. après un changement de clés API).</p>
                  <button type="button" onClick={() => setOliverErrorKey((k) => k + 1)} style={{ padding: '8px 20px', background: 'var(--accent-color, #A49B6D)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    Réessayer
                  </button>
                </div>
              }
            >
              <AIChat historyOpen={oliverHistoryOpen} onCloseHistory={() => setOliverHistoryOpen(false)} newDiscussionTrigger={oliverNewDiscussionTrigger} />
            </ErrorBoundary>
          </div>
        ) : activePage === 'parametres' ? (
          <Parametres defaultTab={parametresDefaultTab} onLogoClick={() => setActivePage('synthese')} />
        ) : (
          <>
            <div className="connection-status">
              <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                {isConnected ? '🟢 Connecté' : '🔴 Déconnecté'}
              </span>
              {!isConnected ? (
                <button onClick={handleConnect} disabled={loading}>
                  {loading ? 'Connexion...' : 'Se connecter à IB'}
                </button>
              ) : (
                <>
                  <button onClick={handleDisconnect} disabled={loading}>
                    Se déconnecter
                  </button>
                  <button onClick={refreshData} disabled={loading} style={{ marginLeft: '10px' }}>
                    {loading ? 'Rechargement...' : 'Rafraîchir'}
                  </button>
                </>
              )}
            </div>

            {isConnected && account && (
              <>
                <div className="metrics-grid">
                  <Metric
                    label="Valeur Nette"
                    value={formatCurrency(account.net_liquidation?.value, account.net_liquidation?.currency, 0)}
                    help="Valeur totale du compte"
                  />
                  <Metric
                    label="Cash Total"
                    value={formatCurrency(account.total_cash?.value, account.total_cash?.currency, 0)}
                    help="Liquidités disponibles"
                  />
                  <Metric
                    label="Valeur Positions"
                    value={formatCurrency(account.gross_position_value?.value, account.gross_position_value?.currency, 0)}
                    help="Valeur totale des positions ouvertes"
                  />
                  <Metric
                    label="P&L Non Réalisé"
                    value={formatCurrency(account.unrealized_pnl?.value, account.unrealized_pnl?.currency, 0)}
                    delta={account.unrealized_pnl?.value}
                    deltaType="normal"
                    currency={account.unrealized_pnl?.currency || 'EUR'}
                    help="Plus/moins-value latente"
                  />
                </div>
              </>
            )}

            {isConnected && positions && positions.length > 0 && (
              <Expander title="📈 Positions Ouvertes" defaultExpanded={true}>
                <Table
                  name="portefeuille"
                  columns={positionsColumns}
                  data={positions}
                  striped={true}
                  hover={true}
                />
              </Expander>
            )}

            {isConnected && positions && positions.length === 0 && (
              <div className="no-positions">
                📭 Aucune position ouverte
              </div>
            )}
          </>
        )}
      </Layout>
    </>
  );
}

export default Dashboard;